import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ChevronDown, User, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProbeReadyDesign = () => {
    setShowToolsDropdown(false);
    navigate('/probe-ready-design');
  };

  const handlePrimerDesignWizard = () => {
    setShowToolsDropdown(false);
    navigate('/primer-design-wizard');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo & Brand Name */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/oliseq-logo.png" 
              alt="OliSeq Logo" 
              className="h-[70px] w-auto"
            />
            <span className="text-2xl font-bold text-gray-900">Primer Probe Hub</span>
          </Link>

          {/* Main Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Info Page (left of Product & Services) */}
            <Link
              to="/info"
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              data-testid="nav-info-link"
            >
              Info Page
            </Link>

            {/* Product & Services Dropdown */}
            <DropdownMenu open={showProductDropdown} onOpenChange={setShowProductDropdown}>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition-colors cursor-pointer">
                Product & Services
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem 
                  onClick={() => { setShowProductDropdown(false); navigate('/ready-assays', { state: { technology: 'realtime_pcr' } }); }}
                  className="cursor-pointer"
                >
                  <div>
                    <div className="font-semibold">Real-Time PCR Ready Assays</div>
                    <div className="text-xs text-gray-500">Pre-designed detection kits</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => { setShowProductDropdown(false); navigate('/ready-assays', { state: { technology: 'digital_pcr' } }); }}
                  className="cursor-pointer"
                >
                  <div>
                    <div className="font-semibold">Digital PCR Ready Assays</div>
                    <div className="text-xs text-gray-500">Pre-designed detection kits</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => { setShowProductDropdown(false); navigate('/workflow'); }}
                  className="cursor-pointer"
                >
                  <div>
                    <div className="font-semibold">Request your Custom Assay</div>
                    <div className="text-xs text-gray-500">Custom primer/probe design service</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Tools Dropdown */}
            <DropdownMenu open={showToolsDropdown} onOpenChange={setShowToolsDropdown}>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition-colors cursor-pointer">
                Tools
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem 
                  onClick={handleProbeReadyDesign}
                  className="cursor-pointer"
                >
                  <div>
                    <div className="font-semibold">Probe Ready Assay Design</div>
                    <div className="text-xs text-gray-500">Design with UPL probe library</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handlePrimerDesignWizard}
                  className="cursor-pointer"
                >
                  <div>
                    <div className="font-semibold">PrimerDesignWizard</div>
                    <div className="text-xs text-gray-500">Custom primer/probe design</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/about" 
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              About
            </Link>

            <Link 
              to="/contact" 
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              Contact
            </Link>
          </div>

          {/* Right Side - User Actions */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Cart */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/cart')} 
                  className="relative"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Cart
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <User className="w-4 h-4 mr-2" />
                      {user.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate('/orders')}>
                      My Orders
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/login')}
                >
                  Login
                </Button>
                <Button 
                  size="sm"
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Menu - Simple for now */}
        <div className="md:hidden pb-4 space-y-2">
          <Link to="/info" className="block text-gray-700 hover:text-blue-600 py-2">
            Info Page
          </Link>
          <button onClick={() => navigate('/ready-assays', { state: { technology: 'realtime_pcr' } })} className="block text-gray-700 hover:text-blue-600 py-2 w-full text-left">
            Real-Time PCR Ready Assays
          </button>
          <button onClick={() => navigate('/ready-assays', { state: { technology: 'digital_pcr' } })} className="block text-gray-700 hover:text-blue-600 py-2 w-full text-left">
            Digital PCR Ready Assays
          </button>
          <button onClick={() => navigate('/workflow')} className="block text-gray-700 hover:text-blue-600 py-2 w-full text-left">
            Request your Custom Assay
          </button>
          <button onClick={handleProbeReadyDesign} className="block text-gray-700 hover:text-blue-600 py-2 w-full text-left">
            Probe Ready Assay Design
          </button>
          <button onClick={handlePrimerDesignWizard} className="block text-gray-700 hover:text-blue-600 py-2 w-full text-left">
            PrimerDesignWizard
          </button>
          <Link to="/about" className="block text-gray-700 hover:text-blue-600 py-2">
            About
          </Link>
          <Link to="/contact" className="block text-gray-700 hover:text-blue-600 py-2">
            Contact
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
